import { useEffect, useState } from "react";
import { api, formatGBP } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, PieChart, Pie, Legend } from "recharts";

const NAVY = ["#0B1528", "#1E293B", "#334155", "#475569", "#64748B", "#94A3B8", "#C5A059"];

export default function ReportsPage() {
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get("/dashboard/summary").then(({ data }) => setSummary(data)); }, []);
  if (!summary) return <div className="text-sm text-slate-500">Loading reports…</div>;

  const status = Object.entries(summary.matter_status).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
  const type = Object.entries(summary.matter_type).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Insights</div>
        <h2 className="font-serif-brand text-3xl">Reports</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card label="Total Clients" value={summary.total_clients} />
        <Card label="Total Matters" value={summary.total_matters} />
        <Card label="Active" value={summary.active_matters} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card label="Revenue Billed" value={formatGBP(summary.revenue_billed)} />
        <Card label="Paid" value={formatGBP(summary.revenue_paid)} />
        <Card label="Outstanding" value={formatGBP(summary.outstanding)} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-slate-200 bg-white rounded-sm p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Matter Status Distribution</div>
          {status.length === 0 ? <div className="text-sm text-slate-500 py-6">No data.</div> : (
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={status} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="value">
                    {status.map((_, i) => <Cell key={i} fill={NAVY[i % NAVY.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="border border-slate-200 bg-white rounded-sm p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Caseload by Type</div>
          {type.length === 0 ? <div className="text-sm text-slate-500 py-6">No data.</div> : (
            <div className="h-72">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={type} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50}>
                    {type.map((_, i) => <Cell key={i} fill={NAVY[i % NAVY.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, accent }) {
  return (
    <div className="border border-slate-200 bg-white rounded-sm p-5">
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</div>
      <div className={`text-2xl font-medium font-mono-num mt-1 ${accent ? "text-[#9A3412]" : "text-[#0B1528]"}`}>{value}</div>
    </div>
  );
}
